<template>
  <div ref="element" class="observer">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

const observer = ref<IntersectionObserver | undefined>(undefined);
const element = ref<HTMLElement | null>();

const emit = defineEmits(['intersect']);

const props = defineProps({
  options: {
    type: Object,
    default: null,
  },
});

onMounted(() => {
  nextTick(() => {
    const observerOptions = props.options || {};
    observer.value = new IntersectionObserver(([entry]) => {
      if (entry && entry.isIntersecting) {
        emit('intersect', { visible: true, ...element.value });
      } else if (entry && !entry.isIntersecting) {
        emit('intersect', { visible: false, ...element.value });
      }
    }, observerOptions);

    if (element.value) {
      observer.value.observe(element.value);

      // 👇 Force trigger right after observing, using getBoundingClientRect
      const rect = element.value.getBoundingClientRect();
      const inViewport =
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0;

      if (inViewport) {
        emit('intersect', { visible: true, ...element.value });
      }
    }
  });
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
  observer.value = undefined;
});

watch(
  () => props.options,
  () => {
    if (observer.value && element.value) {
      observer.value.disconnect();
      observer.value.observe(element.value);
    }
  },
);
</script>

<style scoped lang="postcss">
.observer {
  width: 100%;
  height: auto;
}
</style>
